import {
  Component,
  ChangeDetectorRef,
  Input,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiTableComponent, TableRow, QaiLoadingComponent, SortDirection } from '@qai/ui';
import { finalize, take, tap } from 'rxjs';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { ISimulation } from 'src/app/interfaces/self-serve/responses/isimulation';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/iassignment';
import { CallTypeLabelMap } from 'src/app/utils/constants/self-serve/simulation.constants';
import { formatLongMonthDate } from 'src/app/utils/date-parser.util';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';

const SIMULATION_TABLE_HEADERS = [
  'Name',
  'Product',
  'Published date',
  'Used in',
  'Simulation type',
];

@Component({
  selector: 'published-simulations-table',
  standalone: true,
  templateUrl: './published-simulations-table.component.html',
  styleUrls: ['./published-simulations-table.component.scss'],
  imports: [CommonModule, QaiTableComponent, QaiLoadingComponent, TranslocoModule],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class PublishedSimulationsTableComponent implements OnInit {
  tableData: TableRow[] = [];
  headers: string[] = [...SIMULATION_TABLE_HEADERS];
  isLoading = true;
  initialSortColumn: string = SIMULATION_TABLE_HEADERS[2];
  initialSortDirection: SortDirection = 'desc';
  isTooltipLoading = false;
  private simulations: ISimulation[] = [];
  @ViewChild(QaiTableComponent)
  qaiTableRef: QaiTableComponent | null = null;
  @Input() selectedAccount: { label: string; value: string } | undefined;
  @Input() selectedAccountUuid: string = '';

  constructor(
    private authorService: AuthorService,
    private assignmentsService: AssignmentsService,
    private cdRef: ChangeDetectorRef,
    private translocoService: TranslocoService,
    private localeService: LocaleService
  ) {}

  ngOnInit(): void {
    this.translocoService.selectTranslation('simulationBuilder').pipe(take(1)).subscribe(() => {
      this.headers = [
        this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_NAME'),
        this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_PRODUCT'),
        this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_DATE_PUBLISHED'),
        this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_USED_IN'),
        this.translocoService.translate('simulationBuilder.SIMULATIONS_COL_TYPE'),
      ];
      this.initialSortColumn = this.headers[2];
      if (this.simulations.length > 0) {
        this.tableData = this.transformApiDataToTableRows(this.simulations);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAccount']) {
      this.loadSimulations();
    }
  }

  private loadSimulations() {
    const accountUuid = this.selectedAccount?.value || this.selectedAccountUuid;

    if (!accountUuid) {
      console.error('No valid account UUID found to load simulations.');
      this.isLoading = false;
      return;
    }

    this.authorService
      .findAllSimulations(accountUuid, 'PUBLISHED')
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: (simulations) => {
          this.simulations = simulations;
          this.tableData = this.transformApiDataToTableRows(simulations);
        },
        error: (err: any) => {
          console.error('Error loading simulations:', err);
          this.isLoading = false;
        },
      });
  }

  private transformApiDataToTableRows(apiData: ISimulation[]): TableRow[] {
    return apiData
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .map((simulation) =>
        this.createSimulationTableRow(
          simulation.uuid,
          simulation.name,
          simulation.moduleName,
          formatLongMonthDate(simulation.publishedAt, this.localeService.getCurrentLocaleId()),
          this.formatAssignmentCount(simulation.assignmentCount),
          this.getCallTypeLabel(simulation.callType)
        )
      );
  }

   onTooltipDataRequested = ({ row }: { row: { id: string | number } }) => {
    const simulationId = String(row.id);
    const simulation = this.simulations.find((sim) => sim.uuid === simulationId);
    const moduleUuid = simulation?.moduleUuid || '';

    this.isTooltipLoading = true;

    this.assignmentsService.findAssignmentsForSimulation(simulationId, moduleUuid)
      .pipe(
        tap((res) =>
          console.log(`Fetched all assignments for simulation ${simulationId}`, res)
        ),
        finalize(() => {
          this.isTooltipLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: (assignments: IAssignment[]) => {
          const assignmentNames = assignments.map((a) => a.name || 'Untitled');
          this.qaiTableRef?.setTooltipData(assignmentNames);
        },
        error: (err: any) => {
          console.error(`Failed to fetch assignments for simulation ${simulationId}:`, err);
          this.qaiTableRef?.setTooltipData([this.translocoService.translate('simulationBuilder.ERROR_LOADING_ASSIGNMENTS')]);
        },
      });
  };


  private getCallTypeLabel(callType?: string): string {
    if (!callType) return this.translocoService.translate('simulationBuilder.CALL_TYPE_UNKNOWN');

    // SAFE: internal call-type enum used as lookup key
    const key = callType.toLowerCase() as keyof typeof CallTypeLabelMap;
    return CallTypeLabelMap[key] || this.capitalizeFirstLetter(callType);
  }

  private capitalizeFirstLetter(value: string): string {
    // SAFE: fallback capitalization of internal call-type enum value
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  createSimulationTableRow(
    id: string | number,
    name: string,
    moduleName: string,
    date: string,
    usage: string,
    simulationType: string
  ): TableRow {
    const row = {
      id,
      [this.headers[0]]: { type: 'text', value: name },
      [this.headers[1]]: { type: 'text', value: moduleName },
      [this.headers[2]]: { type: 'text', value: date },
      [this.headers[3]]: { type: 'tooltip', value: [], label: usage },
      [this.headers[4]]: {
        type: 'badge',
        shape: 'rectangle',
        kind: 'gray',
        label: simulationType,
      },
    };
    return { ...row } as unknown as TableRow;
  }

  private formatAssignmentCount(assignmentCount: number): string {
    if (!assignmentCount || assignmentCount === 0) {
      return this.translocoService.translate('simulationBuilder.ASSIGNMENT_COUNT_NONE');
    }

    return this.translocoService.translate(
      assignmentCount > 1
        ? 'simulationBuilder.ASSIGNMENT_COUNT_PLURAL'
        : 'simulationBuilder.ASSIGNMENT_COUNT_SINGULAR',
      { count: assignmentCount }
    );
  }
}
